import { createApp } from 'vue';
import pinia from './stores';
import { configure } from 'vue-gtag';
import * as Sentry from '@sentry/vue';
import dayjs from '@/helpers/dayjs';

import '/node_modules/flag-icons/css/flag-icons.min.css';
import '/node_modules/pikaday/css/pikaday.css';
import '/node_modules/vue-search-select/dist/VueSearchSelect.css';
import './app.sass';
import initDirectives from './directives';

import App from './App.vue';
import router from './router';
import i18n from './languages/i18n';

try {
  const slug = i18n.global.locale.value.split('-')[0];
  document.documentElement.setAttribute('lang', slug);
} catch (error) {
  console.warn('Error setting document language:', error);
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    dayjs: typeof dayjs;
    _dayjs: dayjs.Dayjs;
  }
}

const app = createApp(App);

initDirectives(app);

/* Sentry */
if (import.meta.env.VITE_APP_ENV && import.meta.env.VITE_APP_ENV !== 'development') {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 0.1,
    environment: import.meta.env.VITE_APP_ENV,
    beforeSend(event) {
      const ignoreErrors = [
        'Failed to fetch dynamically imported module',
        'Importing a module script failed',
        'Unable to preload CSS',
        'Request failed with status code 401',
      ];

      for (const error of ignoreErrors) {
        if (event.exception?.values?.[0]?.value?.includes(error)) {
          return null;
        }
      }

      return event;
    },
  });
}
/* End Sentry */

/* Fontawesome */
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretUp,
  faCaretDown,
  faUser,
  faMapMarkerAlt as fasMapMarkerAlt,
  faList,
  faExclamationTriangle,
  faPlus,
  faMinus,
  faArrowLeft,
  faArrowRight,
  faCircle,
  faSync,
  faTerminal,
  faLongArrowLeft,
  faDownload as fasDownload,
  faCheck,
  faEdit as fasEdit,
  faArrowRotateLeft,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faBars,
  faSearch,
  faShoppingBag,
  faTimes,
  faCalendar,
  faInfoCircle,
  faAngleUp,
  faAngleDown,
  faMapMarkerAlt,
  faDownload,
  faChevronUp,
  faChevronDown,
  faEdit,
  faPaperclip,
  faPen,
  faFilePdf,
  faFileExcel,
  faEyeSlash,
  faClock,
  faWarehouseFull,
} from '@fortawesome/pro-regular-svg-icons';
import { faTruck, faBoxTaped } from '@fortawesome/sharp-regular-svg-icons';
import { faInstagram, faFacebook, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(
  faInstagram,
  faFacebook,
  faYoutube,
  faCaretDown,
  faCaretUp,
  faBars,
  faSearch,
  faUser,
  faMapMarkerAlt,
  fasMapMarkerAlt,
  faShoppingBag,
  faTimes,
  faCalendar,
  faInfoCircle,
  faList,
  faAngleUp,
  faAngleDown,
  faMapMarkerAlt,
  faDownload,
  fasDownload,
  faExclamationTriangle,
  faChevronUp,
  faChevronDown,
  faEdit,
  fasEdit,
  faPaperclip,
  faPlus,
  faMinus,
  faArrowLeft,
  faArrowRight,
  faCircle,
  faSync,
  faTerminal,
  faPen,
  faLongArrowLeft,
  faFilePdf,
  faFileExcel,
  faCheck,
  faEyeSlash,
  faClock,
  faWarehouseFull,
  faTruck,
  faBoxTaped,
  faArrowRotateLeft
);

app.component('font-awesome-icon', FontAwesomeIcon);
/* Fontawesome End */

app.config.globalProperties.dayjs = dayjs;
app.config.globalProperties._dayjs = dayjs();

configure({
  tagId: 'UA-24410700-11',
  initMode: 'manual',
  config: {
    includes: [
      { id: 'G-0JL5WRL6K0' }, // Benders Kundportal - GA4
    ],
    appName: 'Benders Kundportal',
  },
  pageTracker: {
    router: router,
    useScreenview: import.meta.env.VITE_APP_ENV !== 'development',
  },
});

app.use(pinia);
app.use(router);
app.use(i18n);

app.mount('#app');
